import { supabase } from '@/integrations/supabase/client';

/**
 * Check if the current session is valid and not expired
 */
export const isSessionValid = async (): Promise<boolean> => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      return false;
    }
    
    // Check if session is expired
    const expiresAt = session.expires_at;
    if (expiresAt) {
      const expirationTime = expiresAt * 1000; // Convert to milliseconds
      const currentTime = Date.now();
      
      if (currentTime >= expirationTime) {
        return false;
      }
    }
    
    return true;
  } catch (error) {
    console.error('Error checking session validity:', error);
    return false;
  }
};

/**
 * Get time until session expires in milliseconds
 */
export const getTimeUntilSessionExpires = async (): Promise<number | null> => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session || !session.expires_at) {
      return null;
    }
    
    const expirationTime = session.expires_at * 1000;
    const currentTime = Date.now();
    const timeUntilExpiration = expirationTime - currentTime;
    
    return Math.max(0, timeUntilExpiration);
  } catch (error) {
    console.error('Error getting session expiration time:', error);
    return null;
  }
};

/**
 * Refresh the current session
 */
export const refreshSession = async (): Promise<boolean> => {
  try {
    const { data, error } = await supabase.auth.refreshSession();
    
    if (error) {
      console.error('Error refreshing session:', error);
      
      // Handle rate limiting specifically
      if (error.message?.includes('429') || error.message?.includes('rate limit')) {
        console.warn('Rate limit reached for token refresh, will retry later');
        return false;
      }
      
      return false;
    }
    
    return !!data.session;
  } catch (error) {
    console.error('Error refreshing session:', error);
    return false;
  }
};

/**
 * Validate and refresh session if needed
 */
export const validateAndRefreshSession = async (): Promise<boolean> => {
  try {
    const isValid = await isSessionValid();
    
    if (!isValid) {
      // Try to refresh the session
      return await refreshSession();
    }
    
    // Check if session is about to expire (within 10 minutes)
    const timeUntilExpires = await getTimeUntilSessionExpires();
    
    if (timeUntilExpires !== null && timeUntilExpires < 10 * 60 * 1000) {
      // Session expires soon, try to refresh
      console.log('Session expires soon, attempting refresh');
      return await refreshSession();
    }
    
    return true;
  } catch (error) {
    console.error('Error validating and refreshing session:', error);
    return false;
  }
};

/**
 * Get session information for debugging
 */
export const getSessionInfo = async () => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      return { hasSession: false };
    }
    
    const expiresAt = session.expires_at;
    const currentTime = Date.now();
    const expirationTime = expiresAt ? expiresAt * 1000 : null;
    const timeUntilExpiration = expirationTime ? expirationTime - currentTime : null;
    
    return {
      hasSession: true,
      userId: session.user.id,
      email: session.user.email,
      expiresAt: expirationTime,
      timeUntilExpiration,
      isExpired: timeUntilExpiration !== null ? timeUntilExpiration <= 0 : false,
      expiresSoon: timeUntilExpiration !== null ? timeUntilExpiration < 5 * 60 * 1000 : false
    };
  } catch (error) {
    console.error('Error getting session info:', error);
    return { hasSession: false, error: error.message };
  }
}; 